import { MoreOutlined } from "@ant-design/icons";
import { useDeclineBrand } from "@entities/brief/lib/hooks/useDeclineBrand";
import { useDeclineInfluencer } from "@entities/brief/lib/hooks/useDeclineInfluencer";
import { useDeleteInfluencerV2 } from "@entities/brief/lib/hooks/useDeleteInfluencerV2";
import { NegotiateBrandCommercialsPopover } from "@features/briefInfluencerCommercials/negotiateBrandCommercials/NegotiateBrandCommercialsPopover";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button, Dropdown, Input, message, Modal } from "antd";
import React, { useEffect, useState } from "react";

import { actionsEnum } from "@entities/brief/lib/constants";
import { ToastService } from "@shared/lib/services";

import { useSkipConfirmationMail } from "@entities/brief/lib/hooks/useSkipConfirmationMail";
import { PurchaseConfirmation } from "@features/purchaseConfirmation/PurchaseConfirmation";
import styles from "./styles.module.scss";

interface Props {
  briefId: string;
  briefInfluencer: IBriefInfluencerV2;
  actions: actionsEnum[];
  onChangeContact?: () => void;
  onView?: () => void;
}

export const BriefInfluencerDropDown: React.FC<Props> = ({
  briefId,
  briefInfluencer,
  actions,
  onChangeContact,
  onView,
}) => {
  const [modal, setModal] = useState<
    null | "decline" | "purchase" | null | "skipConfirmation"
  >(null);
  const [declineType, setDeclineType] = useState<null | "brand" | "creator">(
    null
  );
  const [reason, setReason] = useState("");
  const [isNegotiatePopoverOpen, setIsNegotiatePopoverOpen] = useState(false);

  const {
    decline: declineBrand,
    isDeclining: isDecliningBrand,
    isDeclined: isDeclinedBrand,
  } = useDeclineBrand(briefId);
  const {
    decline: declineInfluencer,
    isDeclining: isDecliningInfluencer,
    isDeclined: isDeclinedInfluencer,
  } = useDeclineInfluencer(briefId);
  const { deleteInfluencer, isDeleting: isDeletingInfluencer } =
    useDeleteInfluencerV2(briefId);
  const [purchaseResult, setPurchaseResult] = useState<{
    nextActionToTake: string;
    result: {
      status: string;
      reason: string;
      briefInfluencer: IBriefInfluencerV2;
    }[];
  } | null>(null);

  const {
    skipConfirmationMail,
    isSkippingConfirmationMail,
    isSkippedConfirmationMail,
  } = useSkipConfirmationMail(briefId);

  useEffect(() => {
    if (isDeclinedBrand && modal === "decline" && declineType === "brand") {
      setModal(null);
      setReason("");
      ToastService.success("Brand declined");
    }
    if (
      isDeclinedInfluencer &&
      modal === "decline" &&
      declineType === "creator"
    ) {
      setModal(null);
      setReason("");
      ToastService.success("Creator declined");
    }
  }, [isDeclinedBrand, isDeclinedInfluencer, modal, declineType]);

  useEffect(() => {
    if (isSkippedConfirmationMail && modal === "skipConfirmation") {
      setModal(null);
      ToastService.success("Skipped confirmation");
    }
  }, [isSkippedConfirmationMail, modal]);

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "Brand Declined") {
      setDeclineType("brand");
      setModal("decline");
    } else if (key === "Creator Declined") {
      setDeclineType("creator");
      setModal("decline");
    } else if (key === "Purchase") {
      setModal("purchase");
    } else if (key === "Delete") {
      Modal.confirm({
        title: "Confirm Delete",
        content: "Are you sure you want to delete this creator?",
        okText: "Delete",
        okType: "danger",
        onOk: async () => {
          try {
            await deleteInfluencer([briefInfluencer._id]);
            ToastService.success("Deleted successfully");
          } catch (e) {
            message.error("Delete failed");
          }
        },
      });
    } else if (key === "Request Brand Negotiation") {
      setIsNegotiatePopoverOpen(true);
    } else if (key === "Skip Confirmation") {
      setModal("skipConfirmation");
    } else if (key === actionsEnum.CHANGE_CONTACT) {
      onChangeContact?.();
    } else if (key === actionsEnum.VIEW) {
      onView?.();
    }
  };

  const handleDecline = async () => {
    if (declineType === "brand") {
      await declineBrand({
        reason,
        briefInfluencerId: briefInfluencer._id,
      });
    } else if (declineType === "creator") {
      await declineInfluencer({
        reason,
        briefInfluencerId: briefInfluencer._id,
      });
    }
  };

  const handleSkipConfirmation = async () => {
    await skipConfirmationMail({
      briefInfluencerIds: [briefInfluencer._id],
    });
  };

  const menuItems = actions.map((action) => ({
    key: action,
    label: action,
  }));

  if (actions.length === 0) return null;

  return (
    <>
      <NegotiateBrandCommercialsPopover
        open={isNegotiatePopoverOpen}
        briefInfluencer={briefInfluencer}
        onOpenChange={setIsNegotiatePopoverOpen}
      >
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={["click", "hover"]}
          placement="bottomRight"
          overlayClassName={styles.dropdownOverlay}
        >
          <Button
            icon={<MoreOutlined />}
            type="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </Dropdown>
      </NegotiateBrandCommercialsPopover>
      <Modal
        open={modal === "decline"}
        title="Confirm"
        onCancel={() => setModal(null)}
        onOk={handleDecline}
        okText="Continue"
        confirmLoading={
          (declineType === "brand" && isDecliningBrand) ||
          (declineType === "creator" && isDecliningInfluencer)
        }
        okButtonProps={{ disabled: !reason }}
      >
        <div style={{ marginBottom: 16 }}>
          Are you sure you want to drop the creator?
        </div>
        <div style={{ marginBottom: 8 }}>Enter the reason for Drop</div>
        <Input
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
      <Modal
        open={modal === "purchase"}
        title="Confirm"
        onCancel={() => setModal(null)}
        footer={null}
        width={1500}
        // onOk={handlePurchase}
        // okText="Continue"
        // confirmLoading={isPurchasing}
      >
        {/* <ConfirmPurchase /> */}

        <PurchaseConfirmation
          onClose={() => setModal(null)}
          briefInfluencers={[briefInfluencer]}
        />
      </Modal>
      <Modal
        open={modal === "skipConfirmation"}
        title="Skip Confirmation"
        onCancel={() => setModal(null)}
        onOk={handleSkipConfirmation}
      >
        <div>
          <div>
            <b>Are you sure you want to skip confirmation?</b>
          </div>
        </div>
      </Modal>
    </>
  );
};
